import { useState } from "react";
import { Check, Plus, X } from "lucide-react";
import { ProgressBar } from "../ui-bits";
import { cn } from "../../lib/utils";

function ChecklistItemRow({ item, onToggle, onRemove }) {
  return (
    <div data-testid={`checklist-item-${item.id}`} className="flex items-center gap-2 py-1 group">
      <button data-testid={`checklist-toggle-${item.id}`} onClick={() => onToggle(item.id)}
        className={cn("h-4 w-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors",
          item.done ? "bg-[#10B981] border-[#10B981] text-white" : "border-muted-foreground/50 hover:border-[#10B981]")}>
        {item.done && <Check size={10} strokeWidth={3} />}
      </button>
      <span className={cn("text-sm flex-1 truncate", item.done && "line-through text-muted-foreground")}>{item.text}</span>
      <button data-testid={`checklist-delete-${item.id}`} onClick={() => onRemove(item.id)}
        className="h-5 w-5 hidden group-hover:flex items-center justify-center rounded text-muted-foreground hover:text-[#E31E24] transition-colors shrink-0">
        <X size={12} />
      </button>
    </div>
  );
}

export default function ChecklistSection({ task, patch }) {
  const [newItem, setNewItem] = useState("");
  const items = task.checklist || [];
  const doneCount = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((doneCount / items.length) * 100) : 0;

  const save = (next) => patch({ checklist: next });
  const toggle = (id) => save(items.map((i) => (i.id === id ? { ...i, done: !i.done } : i)));
  const remove = (id) => save(items.filter((i) => i.id !== id));

  const add = () => {
    if (!newItem.trim()) return;
    save([...items, { id: crypto.randomUUID(), text: newItem.trim(), done: false }]);
    setNewItem("");
  };

  return (
    <div className="p-4 border-b border-border" data-testid="panel-checklist">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Checklist{items.length > 0 && ` · ${doneCount}/${items.length}`}</p>
        {items.length > 0 && <span className="text-xs font-semibold text-muted-foreground">{pct}%</span>}
      </div>
      {items.length > 0 && <ProgressBar value={pct} className="mb-3" />}
      <div className="space-y-1">
        {items.map((item) => (
          <ChecklistItemRow key={item.id} item={item} onToggle={toggle} onRemove={remove} />
        ))}
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Plus size={14} className="text-muted-foreground shrink-0" />
        <input data-testid="checklist-add-input" value={newItem} onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add checklist item, press Enter"
          className="flex-1 h-7 px-2 text-sm rounded border border-border bg-transparent focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary" />
      </div>
    </div>
  );
}
